import { isEmpty, map } from 'es-toolkit/compat';
import React from 'react';

import { toWorkflowResultsExportContext } from 'components/ResultsExport/workflowResultsExportContext';
import { useResultsExportContext } from 'components/ResultsExport/useResultsExportContext';
import { ResultsMode } from 'interfaces/experimentResults';
import { Study } from 'interfaces/study';
import { FeaturesExportGroup } from 'services/export/exportFeaturesForGroups';
import { workflowDirectRunsFailedGuidance, workflowResultsCheckingMessage } from 'services/export/workflowExportSource';
import { runFeaturesExport } from 'services/export/runFeaturesExport';
import { useStudyExportController } from 'services/export/useStudyExportController';
import { getFeatureRequestGroups, ResultPick } from 'services/export/workflowResultsSelection';
import { useFormatBracketsOptions } from 'utils/formatBrackets/formatBracketsOptions';
import { useStudyStainTypeIds } from 'utils/queryHooks/stainType/useStudyStainTypes';

import { isWorkflowExportResolvingMessage, WorkflowDirectExportStatus } from './WorkflowDirectExportStatus';
import { useWorkflowResultsSelection } from './useWorkflowResultsSelection';
import { WorkflowRunsErrorAlert } from './WorkflowRunsErrorAlert';

const toResultsMode = (pick: ResultPick) =>
  pick.source === 'published'
    ? ResultsMode.Published
    : pick.source === 'approved'
    ? ResultsMode.InternallyApproved
    : ResultsMode.Manual;

// The Review button calls `downloadFeatures`. Resolution waits until that call, then sends the default export.
export const useWorkflowFeaturesDirectDownload = (study?: Study) => {
  const [requested, setRequested] = React.useState(false);
  const baseContext = useResultsExportContext(study);
  const controller = useWorkflowResultsSelection('features', study, {
    ignoreStoredChoice: true,
    enabled: requested,
  });
  const { source, selection, panelsInput, getRunLabel } = controller;
  const { startExport, exportProgressDialog, isExportDialogOpen } = useStudyExportController(() => {
    controller.close();
    setRequested(false);
  });
  const { studyId, labId } = baseContext;
  const { data: stainTypesInStudy, isLoading: isLoadingStainTypes } = useStudyStainTypeIds(studyId, {
    enabled: requested && Boolean(studyId),
  });
  const formatBracketsOptions = useFormatBracketsOptions({
    addStain: requested && (stainTypesInStudy?.length ?? 0) > 1,
    studyId: requested ? studyId ?? '' : '',
  });
  const started = React.useRef(false);

  const describeSource = React.useCallback(
    (pick: ResultPick) =>
      pick.source === 'published'
        ? 'Published results'
        : pick.source === 'approved'
        ? 'Approved results'
        : getRunLabel(pick.runId as string),
    [getRunLabel]
  );
  const exportGroups = React.useMemo(
    (): FeaturesExportGroup[] =>
      map(getFeatureRequestGroups(selection), ({ key, panelName, pick, slideIds }) => {
        const resultsMode = toResultsMode(pick);
        const { casesParamsWithoutPaginationAndSorting } = toWorkflowResultsExportContext(baseContext, {
          resultsMode,
          orchestrationId: pick.runId,
        });
        return {
          key,
          title: `${panelName}, ${describeSource(pick)}`,
          fileStem: `${panelName}_${pick.source === 'run' ? `run_${pick.runId}` : pick.source}`,
          resultsMode,
          orchestrationId: pick.runId,
          casesParams: { ...casesParamsWithoutPaginationAndSorting, slideIdsToInclude: slideIds },
        };
      }),
    [baseContext, describeSource, selection]
  );

  const { availability, hasNoResults } = source;
  const isUnavailableForUser =
    availability.disabled && (!source.canViewUnpublishedResults || !study?.allowExport || hasNoResults);
  // A failed run list leaves no Approved or run export groups. Published ones need no list and keep exportGroups non-empty.
  const runsListFailed =
    requested &&
    !isUnavailableForUser &&
    source.hasRunsError &&
    isEmpty(source.runs) &&
    !source.isRefreshingRuns &&
    !panelsInput.isLoading &&
    !panelsInput.isFetching &&
    !panelsInput.error &&
    exportGroups.length === 0;
  const blockedReason = !requested
    ? undefined
    : runsListFailed
    ? workflowDirectRunsFailedGuidance
    : availability.disabled
    ? availability.note
    : panelsInput.isLoading || panelsInput.isFetching
    ? 'Loading panel information across the cohort…'
    : source.isRefreshingRuns
    ? workflowResultsCheckingMessage
    : panelsInput.error
    ? 'The panels could not be loaded, so the slides cannot be grouped by panel.'
    : formatBracketsOptions.isLoading || isLoadingStainTypes
    ? 'Loading feature names…'
    : exportGroups.length === 0
    ? 'Select at least one slide with a result to export.'
    : undefined;

  const downloadFeatures = React.useCallback(() => {
    started.current = false;
    setRequested(true);
  }, []);

  React.useEffect(() => {
    if (!requested || started.current || blockedReason || isUnavailableForUser || !studyId || exportGroups.length === 0)
      return;
    const [firstGroup] = exportGroups;
    started.current = true;
    void startExport({
      title: 'Features',
      canCancel: true,
      closeOnSuccess: true,
      run: runFeaturesExport({
        studyId,
        context: {
          ...formatBracketsOptions,
          orchestrationId: firstGroup?.orchestrationId ?? '',
          skipPreloadingStudyStains: isLoadingStainTypes,
          labId,
          studyId,
        },
        resultsMode: firstGroup?.resultsMode ?? ResultsMode.Published,
        orchestrationId: firstGroup?.orchestrationId,
        hideExcluded: true,
        groups: exportGroups,
        highlightedOnly: false,
      }),
    });
  }, [
    blockedReason,
    exportGroups,
    formatBracketsOptions,
    isLoadingStainTypes,
    isUnavailableForUser,
    labId,
    requested,
    startExport,
    studyId,
  ]);

  const close = () => {
    controller.close();
    setRequested(false);
  };
  const statusMessage = isUnavailableForUser ? availability.note : blockedReason || 'Preparing the features export…';
  const featuresExportUi = requested ? (
    <>
      {!isExportDialogOpen ? (
        <WorkflowDirectExportStatus
          title="Export features"
          message={statusMessage}
          resolving={!isUnavailableForUser && isWorkflowExportResolvingMessage(statusMessage)}
          onClose={close}
        >
          {runsListFailed ? (
            <WorkflowRunsErrorAlert kind="features" studyId={studyId} labId={labId} error={source.runsError} />
          ) : null}
        </WorkflowDirectExportStatus>
      ) : null}
      {exportProgressDialog}
    </>
  ) : null;

  return { downloadFeatures, featuresExportUi };
};
